import React from 'react';
import { AlertTriangle, Clock, Shield } from 'lucide-react';
import { useDegradedMode } from '../contexts/DegradedModeContext';

export const DegradedModeBanner: React.FC = () => {
  const { isDegraded, gracePeriodRemaining, degradedReason } = useDegradedMode();

  if (!isDegraded) {
    return null;
  }

  const minutes = Math.floor(gracePeriodRemaining / 60);
  const seconds = gracePeriodRemaining % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Color based on time remaining
  const urgency = gracePeriodRemaining > 180 ? 'warning' : 'critical';
  const bgColor = urgency === 'warning' ? 'bg-yellow-500/10' : 'bg-red-500/10';
  const borderColor = urgency === 'warning' ? 'border-yellow-500/30' : 'border-red-500/30';
  const textColor = urgency === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400';

  return (
    <div className={`${bgColor} ${borderColor} border-l-4 p-4 mb-4`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className={`h-5 w-5 ${textColor}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${textColor}`}>
            Read-Only Mode Active
          </h3>
          <div className="mt-2 text-sm text-muted-foreground">
            <p className="mb-2">{degradedReason || 'Authentication session issue detected.'}</p>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="font-mono font-semibold">{timeDisplay}</span>
                <span>remaining</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Read-only access only</span>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <p className="text-xs">
                <strong>What you can do:</strong> View existing data, read documentation, access help
              </p>
              <p className="text-xs">
                <strong>What's blocked:</strong> Creating or modifying data, accessing sensitive information
              </p>
              <p className="text-xs mt-2">
                You will be automatically logged out when the timer expires. Please refresh your browser or log in again to restore full access.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
